import { useEffect, useRef, useState, type FormEvent } from 'react';
import { useTranslation } from 'react-i18next';
import { FloppyDiskIcon } from '@phosphor-icons/react/dist/csr/FloppyDisk';
import { XIcon } from '@phosphor-icons/react/dist/csr/X';

import { deviceModelLabel, mapDetails } from './deviceFormHelpers';
import { Button } from '../../design-system/primitives/Button/Button';
import { Dialog } from '../../design-system/composites/Dialog/Dialog';
import { Input } from '../../design-system/primitives/Input/Input';
import { Select, type SelectOption } from '../../design-system/primitives/Select/Select';
import {
  ApiError,
  NETWORK_ERROR_CODE,
  PAGE_EXPIRED_CODE,
  TOO_MANY_REQUESTS_CODE,
} from '../../lib/api/client';
import {
  deviceModelsApi,
  readersApi,
  sensorsApi,
  type DeviceModel,
  type Reader,
  type ReaderStatus,
  type Sensor,
  type SensorStatus,
} from '../../lib/api/devices';
import styles from './DeviceFormDialog.module.css';

/**
 * The create/edit form for a sensor OR a reader — one component,
 * parameterised on `kind`, following `DispensaryFormDialog.tsx` for the
 * overall shape: `node` absent is create, present is edit.
 *
 * ## `status` is a CREATE-ONLY field
 *
 * `SensorUpdateInput`/`ReaderUpdateInput` (`devices.ts`'s own docblock) are
 * typed with `Omit<..., 'status'>` — `PATCH` is refused a `status` outright,
 * because moving a device is `POST .../status`'s job alone (the only path
 * guaranteed to also write a `device_events` row). This form therefore
 * renders the status Select only when `node` is `undefined`; an edit never
 * shows it and never sends it. `DeviceStatusDialog` is the only place a
 * status changes after creation.
 *
 * ## The field set diverges by kind, deliberately
 *
 * `SensorInput` carries `lot_number`/`manufactured_on`/`expires_on` (a
 * sensor's shelf-life); `ReaderInput` carries `model`/`firmware_version`/
 * `warranty_expires_on` (a reader's own facts) — see `devices.ts`'s own
 * docblock for why the two are distinct types rather than a shared shape
 * with unused fields either way.
 */

export interface DeviceFormDialogProps {
  kind: 'sensor' | 'reader';
  open: boolean;
  node?: Sensor | Reader;
  onClose: () => void;
  onSaved: (node: Sensor | Reader) => void;
}

interface FormState {
  serialNumber: string;
  type: string;
  status: string;
  dispensaryId: string;
  notes: string;
  lotNumber: string;
  manufacturedOn: string;
  expiresOn: string;
  model: string;
  firmwareVersion: string;
  warrantyExpiresOn: string;
}

const SENSOR_STATUSES: readonly SensorStatus[] = [
  'in_stock',
  'assigned',
  'dispensed',
  'active',
  'expired',
  'faulty',
  'replaced',
  'returned',
];

const READER_STATUSES: readonly ReaderStatus[] = [
  'in_stock',
  'assigned',
  'dispensed',
  'active',
  'faulty',
  'replaced',
  'returned',
  'retired',
];

function stateFor(node: Sensor | Reader | undefined): FormState {
  if (node === undefined) {
    return {
      serialNumber: '',
      type: '',
      status: 'in_stock',
      dispensaryId: '',
      notes: '',
      lotNumber: '',
      manufacturedOn: '',
      expiresOn: '',
      model: '',
      firmwareVersion: '',
      warrantyExpiresOn: '',
    };
  }
  const sensor = node as Partial<Sensor>;
  const reader = node as Partial<Reader>;
  return {
    serialNumber: node.serial_number,
    type: node.type,
    status: node.status,
    dispensaryId: node.dispensary?.uuid ?? '',
    notes: node.notes ?? '',
    lotNumber: sensor.lot_number ?? '',
    manufacturedOn: sensor.manufactured_on ?? '',
    expiresOn: sensor.expires_on ?? '',
    model: reader.model ?? '',
    firmwareVersion: reader.firmware_version ?? '',
    warrantyExpiresOn: reader.warranty_expires_on ?? '',
  };
}

const FORBIDDEN_CODE = 'forbidden';

export function DeviceFormDialog({ kind, open, node, onClose, onSaved }: DeviceFormDialogProps) {
  const { t, i18n } = useTranslation();
  const isEdit = node !== undefined;

  const [state, setState] = useState<FormState>(() => stateFor(node));
  const [models, setModels] = useState<readonly DeviceModel[]>([]);
  const [missing, setMissing] = useState<readonly string[]>([]);
  const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({});
  const [generalFailure, setGeneralFailure] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);

  const inFlight = useRef(false);
  const serialRef = useRef<HTMLInputElement>(null);

  // The dialog instance is reused across opens (create, then an edit of a
  // different row) rather than remounted — every field resets whenever it
  // opens, keyed on the row it is about to show. Mirrors DispensaryFormDialog.
  useEffect(() => {
    if (!open) return;
    setState(stateFor(node));
    setMissing([]);
    setFieldErrors({});
    setGeneralFailure(null);
  }, [open, node]);

  // The type vocabulary — fetched once per open, filtered to this kind.
  // `deviceModelsApi.list()` is the whole five-row table; a caller narrows
  // it locally rather than the API taking a `kind` query, mirroring how the
  // dropdown itself has no server-side filter to lean on.
  useEffect(() => {
    if (!open) return;
    let cancelled = false;
    void deviceModelsApi.list().then((all) => {
      if (cancelled) return;
      setModels(all.filter((model) => model.kind === kind));
    });
    return () => {
      cancelled = true;
    };
  }, [open, kind]);

  function describeFailure(error: ApiError): string {
    switch (error.code) {
      case FORBIDDEN_CODE:
        return t('devices.notPermitted');
      case TOO_MANY_REQUESTS_CODE:
        return t('devices.throttled');
      case PAGE_EXPIRED_CODE:
        return t('auth.sessionExpired');
      case NETWORK_ERROR_CODE:
        return t('auth.unreachable');
      default:
        return t('devices.actionFailed');
    }
  }

  async function handleSubmit(event: FormEvent<HTMLFormElement>): Promise<void> {
    event.preventDefault();
    if (inFlight.current) return;

    const empty: string[] = [];
    if (state.serialNumber.trim() === '') empty.push('serial_number');
    if (state.type.trim() === '') empty.push('type');

    if (empty.length > 0) {
      setMissing(empty);
      return;
    }
    setMissing([]);

    inFlight.current = true;
    setSubmitting(true);
    setFieldErrors({});
    setGeneralFailure(null);

    try {
      const dispensaryId = state.dispensaryId.trim() === '' ? null : state.dispensaryId.trim();
      const notes = state.notes.trim() === '' ? null : state.notes.trim();

      let saved: Sensor | Reader;
      if (kind === 'sensor') {
        const base = {
          serial_number: state.serialNumber.trim(),
          sensor_type: state.type,
          lot_number: state.lotNumber.trim() === '' ? null : state.lotNumber.trim(),
          manufactured_on: state.manufacturedOn === '' ? null : state.manufacturedOn,
          expires_on: state.expiresOn === '' ? null : state.expiresOn,
          dispensary_id: dispensaryId,
          notes,
        };
        saved =
          isEdit && node !== undefined
            ? await sensorsApi.update(node.uuid, base)
            : await sensorsApi.create({ ...base, status: state.status as SensorStatus });
      } else {
        const base = {
          serial_number: state.serialNumber.trim(),
          reader_type: state.type,
          model: state.model.trim() === '' ? null : state.model.trim(),
          firmware_version: state.firmwareVersion.trim() === '' ? null : state.firmwareVersion.trim(),
          warranty_expires_on: state.warrantyExpiresOn === '' ? null : state.warrantyExpiresOn,
          dispensary_id: dispensaryId,
          notes,
        };
        saved =
          isEdit && node !== undefined
            ? await readersApi.update(node.uuid, base)
            : await readersApi.create({ ...base, status: state.status as ReaderStatus });
      }
      onSaved(saved);
    } catch (error) {
      if (!(error instanceof ApiError)) throw error;
      if (Object.keys(error.details).length > 0) {
        setFieldErrors(mapDetails(error.details));
      } else {
        setGeneralFailure(describeFailure(error));
      }
    } finally {
      inFlight.current = false;
      setSubmitting(false);
    }
  }

  const statuses = kind === 'sensor' ? SENSOR_STATUSES : READER_STATUSES;
  const statusOptions: readonly SelectOption[] = statuses.map((value) => ({
    value,
    label: t(`devices.status.${value}`),
  }));
  const typeOptions: readonly SelectOption[] = models.map((model) => ({
    value: model.code,
    label: deviceModelLabel(model, i18n.language),
  }));

  const dialogTitle =
    kind === 'sensor'
      ? isEdit && node !== undefined
        ? t('devices.editSensorTitle', { serial: node.serial_number })
        : t('devices.createSensorTitle')
      : isEdit && node !== undefined
        ? t('devices.editReaderTitle', { serial: node.serial_number })
        : t('devices.createReaderTitle');

  return (
    <Dialog open={open} size="lg" onClose={onClose} initialFocusRef={serialRef} title={dialogTitle}>
      <form
        className={styles['form']}
        method="post"
        noValidate
        onSubmit={(event) => {
          void handleSubmit(event);
        }}
      >
        <div className={styles['failure']} role="alert" aria-atomic="true" data-testid="deviceFormFailure">
          {generalFailure}
        </div>

        <Input
          ref={serialRef}
          label={t('devices.fieldSerial')}
          width="fill"
          dir="ltr"
          autoCapitalize="none"
          autoCorrect="off"
          spellCheck={false}
          required
          value={state.serialNumber}
          error={missing.includes('serial_number') ? t('devices.required') : fieldErrors['serial_number']}
          onChange={(event) => {
            const { value } = event.target;
            setState((current) => ({ ...current, serialNumber: value }));
          }}
        />

        <Select
          label={t('devices.fieldType')}
          width="fill"
          required
          placeholder={t('devices.selectType')}
          options={typeOptions}
          value={state.type}
          error={missing.includes('type') ? t('devices.required') : fieldErrors['sensor_type'] ?? fieldErrors['reader_type']}
          onChange={(event) => {
            const { value } = event.target;
            setState((current) => ({ ...current, type: value }));
          }}
        />

        {isEdit ? null : (
          <Select
            label={t('devices.fieldStatus')}
            width="fill"
            required
            options={statusOptions}
            value={state.status}
            error={fieldErrors['status']}
            onChange={(event) => {
              const { value } = event.target;
              setState((current) => ({ ...current, status: value }));
            }}
          />
        )}

        <Input
          label={t('devices.fieldDispensary')}
          width="fill"
          dir="ltr"
          value={state.dispensaryId}
          error={fieldErrors['dispensary_id']}
          onChange={(event) => {
            const { value } = event.target;
            setState((current) => ({ ...current, dispensaryId: value }));
          }}
        />

        {kind === 'sensor' ? (
          <>
            <Input
              label={t('devices.fieldLotNumber')}
              width="fill"
              dir="ltr"
              value={state.lotNumber}
              error={fieldErrors['lot_number']}
              onChange={(event) => {
                const { value } = event.target;
                setState((current) => ({ ...current, lotNumber: value }));
              }}
            />
            <Input
              label={t('devices.fieldManufacturedOn')}
              width="fill"
              type="date"
              value={state.manufacturedOn}
              error={fieldErrors['manufactured_on']}
              onChange={(event) => {
                const { value } = event.target;
                setState((current) => ({ ...current, manufacturedOn: value }));
              }}
            />
            <Input
              label={t('devices.fieldExpiresOn')}
              width="fill"
              type="date"
              value={state.expiresOn}
              error={fieldErrors['expires_on']}
              onChange={(event) => {
                const { value } = event.target;
                setState((current) => ({ ...current, expiresOn: value }));
              }}
            />
          </>
        ) : (
          <>
            <Input
              label={t('devices.fieldModel')}
              width="fill"
              dir="ltr"
              value={state.model}
              error={fieldErrors['model']}
              onChange={(event) => {
                const { value } = event.target;
                setState((current) => ({ ...current, model: value }));
              }}
            />
            <Input
              label={t('devices.fieldFirmwareVersion')}
              width="fill"
              dir="ltr"
              value={state.firmwareVersion}
              error={fieldErrors['firmware_version']}
              onChange={(event) => {
                const { value } = event.target;
                setState((current) => ({ ...current, firmwareVersion: value }));
              }}
            />
            <Input
              label={t('devices.fieldWarrantyExpiresOn')}
              width="fill"
              type="date"
              value={state.warrantyExpiresOn}
              error={fieldErrors['warranty_expires_on']}
              onChange={(event) => {
                const { value } = event.target;
                setState((current) => ({ ...current, warrantyExpiresOn: value }));
              }}
            />
          </>
        )}

        <Input
          label={t('devices.fieldNotes')}
          className={styles['wide']}
          width="fill"
          value={state.notes}
          error={fieldErrors['notes']}
          onChange={(event) => {
            const { value } = event.target;
            setState((current) => ({ ...current, notes: value }));
          }}
        />

        <div className={styles['actions']}>
          <Button icon={<FloppyDiskIcon size={16} />} type="submit" loading={submitting}>
            {t('devices.save')}
          </Button>
          <Button icon={<XIcon size={16} />} type="button" variant="ghost" onClick={onClose}>
            {t('devices.cancel')}
          </Button>
        </div>
      </form>
    </Dialog>
  );
}
